/**
 * pict-section-form extension for the `QRCode` input type.
 *
 * Manifest options (descriptor `PictForm`):
 * - `QREntity` — registry entity the value belongs to. Default `Sample`.
 * - `QRSize` — on-screen image size in px. Default 140.
 * - `QRActions` — Edit-theme buttons: any of `Scan`, `Generate`, `Print`. Default `[ 'Print' ]`.
 * - `QRLabelLines` — data addresses (relative to the form's AppData) printed under the code.
 * - `QRProjectAddress` — data address of the project id, used when minting a new code.
 *
 * Per-input state lives in the DOM (keyed by the input's RawHTMLID), never on this provider,
 * because one provider instance serves every QRCode input on the page.
 */

const libPictSectionForm = require('pict-section-form');
const libInputExtension = libPictSectionForm.PictInputExtensionProvider;

class HeadlightQRInput extends libInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	/**
	 * @param {any} pInput
	 * @return {{ Entity: string, Size: number, Actions: Array<string>, LabelLines: Array<string> }}
	 */
	getConfig(pInput)
	{
		const tmpForm = (pInput && pInput.PictForm) || {};
		return {
			Entity: tmpForm.QREntity || 'Sample',
			Size: tmpForm.QRSize || 140,
			Actions: Array.isArray(tmpForm.QRActions) ? tmpForm.QRActions : [ 'Print' ],
			LabelLines: Array.isArray(tmpForm.QRLabelLines) ? tmpForm.QRLabelLines : [],
		};
	}

	/**
	 * @param {string} pRawHTMLID
	 * @param {string} pPrefix
	 * @return {HTMLElement|null}
	 */
	element(pRawHTMLID, pPrefix)
	{
		return (typeof document !== 'undefined') ? document.getElementById(`${pPrefix}${pRawHTMLID}`) : null;
	}

	/**
	 * @param {any} pInput
	 * @param {any} pValue
	 * @param {string} [pRawHTMLID] - Defaults to the input's own; tabular rows pass theirs.
	 */
	paint(pInput, pValue, pRawHTMLID)
	{
		const tmpProvider = this.pict.providers.HeadlightQR;
		const tmpRawHTMLID = pRawHTMLID || (pInput.Macro && pInput.Macro.RawHTMLID);
		const tmpConfig = this.getConfig(pInput);
		const tmpImage = this.element(tmpRawHTMLID, 'HLQR-IMAGE-');
		const tmpValueElement = this.element(tmpRawHTMLID, 'HLQR-VALUE-');
		const tmpValue = (pValue === null || pValue === undefined) ? '' : String(pValue).trim();

		if (tmpImage)
		{
			if (tmpProvider && tmpValue)
			{
				try
				{
					tmpImage.innerHTML = tmpProvider.renderSVG(tmpProvider.encode(tmpConfig.Entity, tmpValue), { SizePX: tmpConfig.Size, Title: `${tmpConfig.Entity} code ${tmpValue}` });
				}
				catch (pError)
				{
					tmpImage.innerHTML = '';
					this.pict.log.warn(`QRCode input ${pInput.Hash} could not render "${tmpValue}": ${pError.message || pError}`);
				}
			}
			else
			{
				tmpImage.innerHTML = `<div class="hlqr-input-empty" style="width:${tmpConfig.Size}px;height:${tmpConfig.Size}px;">No code yet</div>`;
			}
		}
		if (tmpValueElement)
		{
			tmpValueElement.textContent = tmpValue;
		}

		const tmpButtons = this.element(tmpRawHTMLID, 'HLQR-BUTTONS-');
		if (tmpButtons)
		{
			for (const tmpButton of Array.from(tmpButtons.querySelectorAll('[data-hlqr-action]')))
			{
				const tmpAction = tmpButton.getAttribute('data-hlqr-action');
				let tmpVisible = tmpConfig.Actions.includes(tmpAction);
				if (tmpAction === 'Print' && !tmpValue)
				{
					tmpVisible = false;
				}
				if (tmpAction === 'Generate' && tmpValue)
				{
					tmpVisible = false;
				}
				/** @type {HTMLElement} */ (tmpButton).hidden = !tmpVisible;
			}
		}
	}

	/**
	 * @param {any} pInput
	 * @param {string} pMessage
	 */
	setMessage(pInput, pMessage)
	{
		const tmpElement = this.element(pInput.Macro.RawHTMLID, 'HLQR-MESSAGE-');
		if (tmpElement)
		{
			tmpElement.textContent = pMessage || '';
		}
	}

	/**
	 * Write a value to the form data and show it in the input's own field (setDataByInput only
	 * updates the data and fires onDataChange).
	 *
	 * @param {any} pView
	 * @param {any} pInput
	 * @param {string} pValue
	 * @param {string} pTransactionGUID
	 */
	setValue(pView, pInput, pValue, pTransactionGUID)
	{
		pView.setDataByInput(pInput, pValue);
		if (typeof pView.manualMarshalDataToViewByInput === 'function')
		{
			pView.manualMarshalDataToViewByInput(pInput, pTransactionGUID);
		}
		this.paint(pInput, pValue);
	}

	/**
	 * A new code in the customer's format. HeadLight apps mint through the HeadlightQRCodeHash service
	 * (which holds the signed-in customer's profile); elsewhere (the testbed) through HeadlightQR.
	 * `PictForm.QRProjectAddress` points at the project id in AppData, when the form has one.
	 *
	 * @param {any} pInput
	 * @return {string}
	 */
	mintCode(pInput)
	{
		const tmpAppData = this.pict.AppData || {};
		const tmpSession = tmpAppData.Session || {};
		const tmpCurrentUser = tmpAppData.CurrentUser || {};
		const tmpProjectAddress = pInput && pInput.PictForm && pInput.PictForm.QRProjectAddress;
		const tmpIDProject = tmpProjectAddress ? this.pict.manifest.getValueAtAddress(tmpAppData, tmpProjectAddress) : tmpSession.IDProject;
		const tmpContext =
		{
			IDProject: tmpIDProject,
			IDUser: tmpCurrentUser.UserID || tmpSession.UserID,
			ExternalSyncGUID: (tmpAppData.FullUserRecord && tmpAppData.FullUserRecord.ExternalSyncGUID) || tmpSession.ExternalSyncGUID,
			CustomerID: tmpCurrentUser.CustomerID,
			TimeZone: tmpCurrentUser.Timezone || tmpSession.TimeZone,
		};
		const tmpHashService = this.pict.fable.services && this.pict.fable.services.HeadlightQRCodeHash;
		if (tmpHashService && typeof tmpHashService.mintForCustomer === 'function')
		{
			return tmpHashService.mintForCustomer(tmpContext);
		}
		return this.pict.providers.HeadlightQR.mint(tmpContext);
	}

	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.paint(pInput, pValue);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.paint(pInput, pValue);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.paint(pInput, pValue);
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		const tmpProvider = this.pict.providers.HeadlightQR;
		const tmpConfig = this.getConfig(pInput);
		const tmpValue = (pValue === null || pValue === undefined) ? '' : String(pValue).trim();
		if (!tmpProvider)
		{
			return super.onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
		}

		if (pEvent === 'qr-print' && tmpValue)
		{
			const tmpLines = [ tmpValue ].concat(tmpConfig.LabelLines.map((pAddress) => this.pict.manifest.getValueAtAddress(this.pict.AppData, pAddress)).filter((pLine) => pLine !== undefined && pLine !== null && pLine !== '').map(String));
			tmpProvider.printLabels([ { Entity: tmpConfig.Entity, Value: tmpValue, Lines: tmpLines } ]);
		}
		else if (pEvent === 'qr-generate' && !tmpValue)
		{
			const tmpCode = this.mintCode(pInput);
			this.setValue(pView, pInput, tmpCode, pTransactionGUID);
		}
		else if (pEvent === 'qr-scan')
		{
			const tmpOperation = `HeadlightQR-Scan-${pInput.Hash}`;
			pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpOperation);
			const tmpDefinition = tmpProvider.getEntityDefinition(tmpConfig.Entity);
			tmpProvider.scanOnce({ Accept: [ tmpConfig.Entity ], RawEntities: [ tmpConfig.Entity ], Label: `Scan a ${tmpDefinition ? tmpDefinition.Label : 'code'}` })
				.then((pResolution) =>
				{
					if (!pResolution)
					{
						return;
					}
					const tmpScanned = (pResolution.Record && tmpDefinition && pResolution.Record[tmpDefinition.Column]) || pResolution.Decoded.Value;
					if (pResolution.Status === 'Deleted')
					{
						this.setMessage(pInput, 'That label belongs to a deleted record.');
						return;
					}
					this.setMessage(pInput, '');
					this.setValue(pView, pInput, tmpScanned, pTransactionGUID);
				})
				.catch((pError) =>
				{
					this.setMessage(pInput, 'Scanning failed. Try again, or type the code.');
					this.pict.log.error(`QRCode input ${pInput.Hash} scan failed: ${pError.message || pError}`);
				})
				.finally(() =>
				{
					pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpOperation);
				});
		}
		return super.onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}
}

module.exports = HeadlightQRInput;
module.exports.default_configuration = Object.assign({}, libInputExtension.default_configuration || {}, { ProviderIdentifier: 'HeadlightQR-Input' });
